import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { getCurrencySymbol } from "../util/helper";
import { validateToken } from "../services/ApiCalls";
import { getCookie } from "../util/cookies";
import { SESSION_EXPIRED_EVENT, SessionEndReason, clearOpmBrowserData } from "../util/session";
import SessionExpiredModal from "../Components/SessionExpiredModal";
import {
  opmStorage,
  Keys,
  UiKeys,
  GUEST_ELEMENT_SCOPE,
  DEFAULT_TTL_SECONDS,
  removeLegacyGlobalKeys,
  migrateGuestCartToElement,
  tableScopeOf,
} from "../util/opmStorage";
const AppContext = createContext();

const MENU_TTL_SECONDS = 60 * 60;

export const emptyOrderState = () => ({
  items: [],
  order_summary: {},
});

const normalizeOrder = (raw) => {
  if (!raw || typeof raw !== "object") return emptyOrderState();
  if (Array.isArray(raw)) return { items: raw, order_summary: {} };
  return {
    items: Array.isArray(raw.items) ? raw.items : [],
    order_summary: raw.order_summary || {},
  };
};

export const AppProvider = ({ children }) => {
  const getInitialLanguage = () => {
    try {
      return (
        localStorage.getItem(UiKeys.language()) ||
        localStorage.getItem("language") ||
        "he"
      );
    } catch {
      return "he";
    }
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const [orderList, setOrderList] = useState(emptyOrderState);
  const [currentOrderList, setCurrentOrderList] = useState(emptyOrderState);
  const [categoryList, setCategoryList] = useState([]);
  const [selectedTab, setSelectedTab] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [menu, setMenu] = useState(null);
  // The table numbers the guest's session sits at right now, from the server (empty until the guest entered the table code).
  const [tableNumbers, setTableNumbers] = useState([]);
  const [isSplashScreen, setIsSplashScreen] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authClaims, setAuthClaims] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  /**
   * True until the saved table session (if any) has been checked on load. Screens that need
   * the session wait instead of treating the guest as signed out while it is being restored.
   */
  const [authRestoring, setAuthRestoring] = useState(true);
  const [guestRestaurantId, setGuestRestaurantId] = useState(null);
  const [guestBrowseReady, setGuestBrowseReady] = useState(false);
  /** Placed order awaiting payment (cart is cleared after submit). */
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  /** Persisted dining_session_id from order create response (same scope as token / guestName). */
  const [diningSessionId, setDiningSessionId] = useState(null);
  /**
   * Set when the guest's OPM token is no longer valid or their table has closed (`reason`):
   * everything OPM saved is cleared and a blocking popup sends the guest back to this
   * restaurant's menu.
   */
  const [sessionExpired, setSessionExpired] = useState(null);
  const guestRestaurantIdRef = useRef(null);
  guestRestaurantIdRef.current = guestRestaurantId;
  const sessionExpiredRef = useRef(false);

  useEffect(() => {
    const onExpired = (event) => {
      if (sessionExpiredRef.current) return;
      sessionExpiredRef.current = true;
      const reason = event?.detail?.reason || SessionEndReason.EXPIRED;
      // Read the restaurant before the cookies go, so the popup can send the guest back to it.
      const restaurantId =
        guestRestaurantIdRef.current || getCookie("opm_restaurant_id");
      clearOpmBrowserData();
      setSessionExpired({ restaurantId, reason });
      setIsAuthenticated(false);
      setAuthToken(null);
      setAuthClaims(null);
      setDiningSessionId(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const storageElementId = useMemo(() => {
    const scope = tableScopeOf(authClaims);
    if (isAuthenticated && scope) {
      return scope;
    }
    return GUEST_ELEMENT_SCOPE;
  }, [isAuthenticated, authClaims]);

  const lastHydratedScopeRef = useRef(null);
  const legacyMigratedRef = useRef(false);
  const authRestoreAttemptedRef = useRef(false);

  useEffect(() => {
    authRestoreAttemptedRef.current = false;
  }, [guestRestaurantId]);

  useEffect(() => {
    if (legacyMigratedRef.current) return;
    legacyMigratedRef.current = true;
    try {
      const oldLang = localStorage.getItem("language");
      if (oldLang && !localStorage.getItem(UiKeys.language())) {
        localStorage.setItem(UiKeys.language(), oldLang);
      }
    } catch {
      /* ignore */
    }
    removeLegacyGlobalKeys();
    opmStorage.cleanupExpired();
  }, []);

  /** Restore session from namespaced token when cookies match a table. */
  useEffect(() => {
    if (!guestRestaurantId || authRestoreAttemptedRef.current) return;
    const r = guestRestaurantId;
    const e = getCookie("opm_element_id");
    if (!e) {
      authRestoreAttemptedRef.current = true;
      setAuthRestoring(false);
      return;
    }
    const token = opmStorage.getWithTtl(Keys.token(r, e));
    if (!token) {
      authRestoreAttemptedRef.current = true;
      setAuthRestoring(false);
      return;
    }
    let cancelled = false;
    validateToken(token)
      .then((claims) => {
        if (cancelled) return;
        if (
          String(claims.restaurant_id) === String(r) &&
          tableScopeOf(claims) === String(e)
        ) {
          migrateGuestCartToElement(r, String(e));
          setIsAuthenticated(true);
          setAuthClaims(claims);
          setAuthToken(token);
        }
      })
      .catch(() => {
        opmStorage.remove(Keys.token(r, e));
      })
      .finally(() => {
        if (!cancelled) {
          authRestoreAttemptedRef.current = true;
          setAuthRestoring(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [guestRestaurantId]);

  /** Load persisted slice when restaurant / element scope changes. */
  useEffect(() => {
    if (!guestRestaurantId || !storageElementId) return;
    const scopeKey = `${guestRestaurantId}:${storageElementId}`;
    if (lastHydratedScopeRef.current === scopeKey) return;
    lastHydratedScopeRef.current = scopeKey;

    const r = guestRestaurantId;
    const e = storageElementId;

    const o = opmStorage.getWithTtl(Keys.orderList(r, e));
    if (o != null) setOrderList(normalizeOrder(o));
    else setOrderList(emptyOrderState());

    const c = opmStorage.getWithTtl(Keys.currentOrder(r, e));
    if (c != null) setCurrentOrderList(normalizeOrder(c));
    else setCurrentOrderList(emptyOrderState());

    const tab = opmStorage.getWithTtl(Keys.selectedTab(r));
    if (tab != null) setSelectedTab(typeof tab === "string" ? tab : "");

    const item = opmStorage.getWithTtl(Keys.selectedItem(r, e));
    setSelectedItem(item ?? null);

    const rawDs = opmStorage.getWithTtl(Keys.diningSessionId(r, e));
    if (rawDs != null && rawDs !== "") {
      const n = Number(rawDs);
      setDiningSessionId(Number.isFinite(n) ? n : null);
    } else {
      setDiningSessionId(null);
    }
  }, [guestRestaurantId, storageElementId]);

  const direction = useMemo(
    () => (language === "he" ? "rtl" : "ltr"),
    [language]
  );
  const locale = useMemo(
    () => (language === "he" ? "he-IL" : "en-US"),
    [language]
  );
  const currency = menu?.currency || "USD";
  const currencySymbol = getCurrencySymbol(currency);

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
    try {
      if (!opmStorage.writesDisabled) localStorage.setItem(UiKeys.language(), language);
    } catch {
      /* ignore */
    }
  }, [language, direction]);

  useEffect(() => {
    if (!guestRestaurantId) return;
    const r = guestRestaurantId;
    if (selectedTab) {
      opmStorage.setWithTtl(Keys.selectedTab(r), selectedTab, DEFAULT_TTL_SECONDS);
    } else {
      opmStorage.remove(Keys.selectedTab(r));
    }
  }, [selectedTab, guestRestaurantId]);

  useEffect(() => {
    if (!guestRestaurantId) return;
    const r = guestRestaurantId;
    if (menu) {
      opmStorage.setWithTtl(Keys.menu(r), menu, MENU_TTL_SECONDS);
    } else {
      opmStorage.remove(Keys.menu(r));
    }
  }, [menu, guestRestaurantId]);

  useEffect(() => {
    if (!guestRestaurantId || !storageElementId) return;
    const r = guestRestaurantId;
    const e = storageElementId;
    if (selectedItem) {
      opmStorage.setWithTtl(
        Keys.selectedItem(r, e),
        selectedItem,
        DEFAULT_TTL_SECONDS
      );
    } else {
      opmStorage.remove(Keys.selectedItem(r, e));
    }
  }, [selectedItem, guestRestaurantId, storageElementId]);

  useEffect(() => {
    if (!guestRestaurantId || !storageElementId) return;
    opmStorage.setWithTtl(
      Keys.orderList(guestRestaurantId, storageElementId),
      orderList,
      DEFAULT_TTL_SECONDS
    );
  }, [orderList, guestRestaurantId, storageElementId]);

  useEffect(() => {
    if (!guestRestaurantId || !storageElementId) return;
    opmStorage.setWithTtl(
      Keys.currentOrder(guestRestaurantId, storageElementId),
      currentOrderList,
      DEFAULT_TTL_SECONDS
    );
  }, [currentOrderList, guestRestaurantId, storageElementId]);

  return (
    <AppContext.Provider
      value={{
        language,
        setLanguage,
        direction,
        locale,
        currency,
        currencySymbol,
        menu,
        setMenu,
        restaurantName: menu?.restaurant_name || "",
        tableLabel: tableNumbers.join(", "),
        setTableNumbers,
        isSplashScreen,
        setIsSplashScreen,
        orderList,
        setOrderList,
        setCurrentOrderList,
        currentOrderList,
        setCategoryList,
        categoryList,
        setSelectedTab,
        selectedTab,
        selectedItem,
        setSelectedItem,
        isAuthenticated,
        setIsAuthenticated,
        authRestoring,
        authClaims,
        setAuthClaims,
        authToken,
        setAuthToken,
        guestRestaurantId,
        setGuestRestaurantId,
        guestBrowseReady,
        setGuestBrowseReady,
        storageElementId,
        checkoutOrder,
        setCheckoutOrder,
        diningSessionId: diningSessionId,
        setDiningSessionId,
      }}
    >
      {children}
      <SessionExpiredModal
        show={sessionExpired != null}
        restaurantId={sessionExpired?.restaurantId}
        reason={sessionExpired?.reason}
      />
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
